import { FC, useCallback, useMemo } from 'react';
import { RouterLocation } from 'connected-react-router';

import { NumberPages, NumberPagesAction, NumberPagesRenderItem } from '@hh.ru/magritte-ui';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import urlParser from 'bloko/common/urlParser';

import { useSelector } from 'src/hooks/useSelector';
import { Paging } from 'src/models/paging.types';

import styles from './styles.less';

const getPageHrefByPageNumber = (
    pageNumber: number,
    location: RouterLocation<unknown>,
    searchSessionId: string | null
): string => {
    const urlObject = urlParser(location.pathname + location.search);
    urlObject.params.page = pageNumber - 1;
    if (searchSessionId) {
        urlObject.params.searchSessionId = searchSessionId;
    }
    return urlObject.href;
};

interface Pagination {
    paging: Paging;
}

const Pagination: FC<Pagination> = ({ paging }) => {
    const location = useSelector(({ router }) => router.location);
    const searchSessionId = useSelector(({ searchSessionId }) => searchSessionId);
    const currentPage = useMemo(() => (paging.pages.find(({ selected }) => selected)?.page || 0) + 1, [paging]);
    const lastPage = useMemo(
        () => (paging.lastPage?.page ?? paging.pages.reduce((max, item) => Math.max(max, item.page), 0)) + 1,
        [paging]
    );

    const renderItem: NumberPagesRenderItem = useCallback(
        ({ page, ...props }): JSX.Element => {
            return (
                <NumberPagesAction
                    {...props}
                    Element={SPALink}
                    to={getPageHrefByPageNumber(page, location, searchSessionId)}
                    data-qa="pager-page"
                />
            );
        },
        [location, searchSessionId]
    );

    const renderNavigation: NumberPagesRenderItem = useCallback(
        ({ page, isNext, isPrevious, ...props }): JSX.Element => {
            return (
                <NumberPagesAction
                    {...props}
                    isIcon={isNext || isPrevious}
                    Element={SPALink}
                    to={getPageHrefByPageNumber(page, location, searchSessionId)}
                    data-qa={isNext ? 'pager-next' : 'pager-previous'}
                />
            );
        },
        [location, searchSessionId]
    );

    return (
        <div className={styles.wrapper}>
            <NumberPages
                first={1}
                last={lastPage}
                current={currentPage}
                renderItem={renderItem}
                renderNavigation={renderNavigation}
                data-qa="pager-block"
            />
        </div>
    );
};

export default Pagination;
